// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';

// material ui
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

// api
import { useGetBuildingsQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import { usePostResetPredictiveHeatControlMutation } from '@/api/subscription-manager/predictiveHeatControlSlice';

// shared components
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

const HeatingPanicButton = () => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);

  const { data: buildingData = [] } = useGetBuildingsQuery();

  const [
    triggerResetHeatControl,
    { isLoading: isResetLoading, isFetching: isResetFetching },
  ] = usePostResetPredictiveHeatControlMutation();

  const isMutating = isResetLoading || isResetFetching;

  const handleClose = useCallback(() => setOpenDialog(false), []);

  return (
    <>
      <Tooltip
        arrow
        title={
          <span style={{ fontSize: '1rem' }}>
            {t('heatingPanicButton.tooltip', { ns: 'buildingOptimization' })}
          </span>
        }
        placement="bottom"
      >
        <Button
          variant="outlined"
          startIcon={<NotInterestedIcon />}
          sx={{
            backgroundColor: '#ff4545',
            mt: -0.5,
            ml: 1,
            border: '1px solid red',
            color: 'white',
            '&:hover': {
              backgroundColor: 'red',
              border: '1px solid red',
              color: 'white',
            },
          }}
          onClick={() => setOpenDialog(true)}
        >
          {t('heatingPanicButton.label', { ns: 'buildingOptimization' })}
        </Button>
      </Tooltip>

      <DialogPanic
        open={openDialog}
        onClose={handleClose}
        values={buildingData}
        isLoading={isMutating}
        triggerResetHeatControl={triggerResetHeatControl}
      />
    </>
  );
};

const DialogPanic = ({
  open,
  onClose,
  values,
  onChosenItems,
  triggerResetHeatControl,
}) => {
  // Normalize buildings to a consistent shape every time `values` change.
  // Expecting: { Name, JobSubscriptions } (from your code). We expose:
  // { name, jobSubscriptionIds, eligible, count }
  const { t } = useTranslation();
  const items = useMemo(() => {
    return (values ?? []).map((b) => {
      const jobSubscriptionIds = b.JobSubscriptions ?? [];
      const count = jobSubscriptionIds.length;
      return {
        name: b.Name,
        jobSubscriptionIds,
        eligible: count > 0,
        count,
      };
    });
  }, [values]);

  const eligibleItems = useMemo(() => items.filter((i) => i.eligible), [items]);
  const totalEligible = eligibleItems.length;

  const [selectedNames, setSelectedNames] = useState(new Set());

  const normalizedSelected = useMemo(() => {
    const eligibleNames = new Set(eligibleItems.map((i) => i.name));
    const next = new Set();
    for (const name of selectedNames) {
      if (eligibleNames.has(name)) next.add(name);
    }
    return next;
  }, [eligibleItems, selectedNames]);

  const allSelected =
    totalEligible > 0 && normalizedSelected.size === totalEligible;
  const someSelected = normalizedSelected.size > 0 && !allSelected;

  const toggleAll = useCallback(
    (checked) => {
      if (checked) {
        setSelectedNames(new Set(eligibleItems.map((i) => i.name)));
      } else {
        setSelectedNames(new Set());
      }
    },
    [eligibleItems],
  );

  const toggleOne = useCallback(
    (name) => (event) => {
      setSelectedNames((prev) => {
        const next = new Set(prev);
        if (event.target.checked) next.add(name);
        else next.delete(name);
        return next;
      });
    },
    [],
  );

  const chosenItems = useMemo(() => {
    const selectedSet = normalizedSelected;
    return items.filter((i) => selectedSet.has(i.name));
  }, [items, normalizedSelected]);

  const selectedCount = normalizedSelected.size;

  // --- NEW: per-id status tracking ---
  const [inFlightIds, setInFlightIds] = useState(new Set());
  const [successIds, setSuccessIds] = useState(new Set());
  const [errorIds, setErrorIds] = useState(new Set());

  useEffect(() => {
    if (!open) {
      setInFlightIds(new Set());
      setSuccessIds(new Set());
      setErrorIds(new Set());
      setSelectedNames(new Set());
    }
  }, [open]);

  const idsForBuilding = (item) => item.jobSubscriptionIds || [];
  const anyIn = (ids, setLike) => ids.some((id) => setLike.has(Number(id)));

  const extractSucceededIds = (resp) => {
    if (Array.isArray(resp)) {
      return resp
        .filter(
          (r) => r && r.success && Number.isInteger(r.job_subscription_id),
        )
        .map((r) => r.job_subscription_id);
    }
    if (resp && typeof resp === 'object' && 'job_subscription_id' in resp) {
      return resp.success && Number.isInteger(resp.job_subscription_id)
        ? [resp.job_subscription_id]
        : [];
    }
    return [];
  };

  const extractFailedIds = (resp) => {
    if (Array.isArray(resp)) {
      return resp
        .filter(
          (r) =>
            r && r.success === false && Number.isInteger(r.job_subscription_id),
        )
        .map((r) => r.job_subscription_id);
    }
    if (resp && typeof resp === 'object' && 'job_subscription_id' in resp) {
      return resp.success === false &&
        Number.isInteger(resp.job_subscription_id)
        ? [resp.job_subscription_id]
        : [];
    }
    return [];
  };

  const handleAccept = async () => {
    const ids = chosenItems
      .flatMap((i) => i.jobSubscriptionIds)
      .map((x) => parseInt(x, 10))
      .filter((x) => !isNaN(x));

    if (!ids.length) return;

    setSuccessIds(new Set());
    setErrorIds(new Set());
    setInFlightIds(new Set());

    setInFlightIds(new Set(ids));

    try {
      const resp = await triggerResetHeatControl(ids).unwrap();
      const ok = extractSucceededIds(resp);
      const bad = extractFailedIds(resp);

      if (ok.length) {
        setSuccessIds((prev) => {
          const next = new Set(prev);
          ok.forEach((id) => next.add(id));
          return next;
        });
      }
      if (bad.length) {
        setErrorIds((prev) => {
          const next = new Set(prev);
          bad.forEach((id) => next.add(id));
          return next;
        });
      }
    } catch (err) {
      setErrorIds(new Set(ids));
      console.error('Reset failed:', err);
    } finally {
      setInFlightIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      onChosenItems?.(chosenItems);
    }
  };

  const isAnyResetRunning = inFlightIds.size > 0;

  return (
    <InsetDrawer
      open={open}
      onClose={onClose}
      title={t('buildingOptimization:tooltips.seizeControl')}
    >
      <>
        {/* List */}
        <Alert
          variant="standard"
          severity="info"
          sx={{
            borderLeft: '6px solid',
            borderLeftColor: 'info.main' || 'divider',
            mb: 2,
          }}
        >
          <AlertTitle>
            {t('heatingPanicButton.alertTitle', { ns: 'buildingOptimization' })}
          </AlertTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            p={1}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  color="primary"
                  disabled={isAnyResetRunning}
                />
              }
              label="Select All (eligible only)"
            />
            <Stack direction="row" gap={1}>
              <Chip label={`Selected: ${selectedCount}`} />
              <Chip label={`Eligible: ${totalEligible}`} />
              <Chip label={`Total: ${items.length}`} />
            </Stack>
          </Stack>
        </Alert>
        <Stack gap={1} padding={2} maxHeight="65vh" overflow="auto">
          {items.map((item, index) => {
            const isChecked = normalizedSelected.has(item.name);
            const ids = idsForBuilding(item);
            const showSpinner = anyIn(ids, inFlightIds);
            const showSuccess = anyIn(ids, successIds);
            const showError = anyIn(ids, errorIds);
            const itemKey = item.jobSubscriptionIds.join('-') + `-${index}`;

            return (
              <FormControlLabel
                key={itemKey}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={toggleOne(item.name)}
                    color="primary"
                    disabled={!item.eligible || isAnyResetRunning} // lock selection during a run
                  />
                }
                label={
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography>{item.name}</Typography>
                    {!item.eligible && (
                      <Tooltip
                        title={t(
                          'buildingOptimization:tooltips.noActivePredictiveHeatControlSubscriptions',
                        )}
                      >
                        <Chip size="small" label="Not eligible" />
                      </Tooltip>
                    )}

                    {/* Status adornments */}
                    {showSpinner && (
                      <>
                        <CircularProgress size={16} />
                        <Typography>
                          {t('heatingPanicButton.seizingControl', {
                            ns: 'buildingOptimization',
                          })}
                        </Typography>
                      </>
                    )}
                    {showSuccess && !showSpinner && (
                      <>
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography>
                          {t('heatingPanicButton.resetSuccess', {
                            ns: 'buildingOptimization',
                          })}
                        </Typography>
                      </>
                    )}
                    {showError && !showSpinner && (
                      <Tooltip
                        title={t(
                          'buildingOptimization:tooltips.resetFailedForSubscription',
                        )}
                      >
                        <ErrorOutlineIcon fontSize="small" color="error" />
                      </Tooltip>
                    )}
                  </Stack>
                }
              />
            );
          })}
        </Stack>

        {/* Actions */}
        <Stack direction="row" justifyContent="flex-end" p={2} gap={1}>
          <Button variant="text" onClick={onClose} disabled={isAnyResetRunning}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={selectedCount === 0 || isAnyResetRunning}
            onClick={handleAccept}
          >
            {isAnyResetRunning
              ? 'Resetting…'
              : `Reset ${selectedCount} building${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </Stack>
      </>
    </InsetDrawer>
  );
};

export default HeatingPanicButton;
